import { useEffect, useRef } from 'react';

/**
 * Keeps the post page's single-post Apollo cache view consistent with DDB
 * across websocket reconnects.
 *
 * When the websocket drops and reconnects, any new_replies / score /
 * interaction broadcast that fired during the gap is permanently lost to
 * this client — there's no re-send. getPostById on DDB is the canonical
 * source, so we fetch it on reconnect. The fetch updates the Apollo cache
 * (Post:{id}) automatically — every consumer subscribes via useQuery and
 * re-renders without further dispatch (TRIL-191).
 *
 * One effect: trigger the refetch on `authenticated` false -> true. The
 * behavior of the trigger (one fetch per transition, not per render) is
 * unit-tested in `hooks/__tests__/usePostReconnectReconcile.test.tsx`.
 */
export function usePostReconnectReconcile(params: {
  postId: string | undefined;
  authenticated: boolean;
  fetchPostById: () => void;
  // Retained as an optional pass-through after TRIL-191 dropped Effect 2;
  // callers continue to pass the latest getPostById data without breakage,
  // and the test harness uses it to assert no-Redux-dispatch behavior. The
  // hook itself no longer reads it — the refetch alone updates the Apollo
  // cache that consumers subscribe to. Drop in a future cleanup once every
  // caller stops passing it.
  fetchedPostData?: unknown;
}) {
  const { postId, authenticated, fetchPostById } = params;

  // --- Effect 1: refetch on false -> true transition ----------------------
  const wasAuthenticatedRef = useRef(authenticated);
  useEffect(() => {
    const wasAuth = wasAuthenticatedRef.current;
    wasAuthenticatedRef.current = authenticated;
    if (!postId) return;
    if (wasAuth === false && authenticated === true) {
      fetchPostById();
    }
  }, [authenticated, postId, fetchPostById]);
}
